import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import {
  type LatestCreativeClassificationRunQuery,
  useLatestCreativeClassificationRunQuery,
} from "src/graphql";
import {
  type CreativeTaggingNamespace,
  creativeTaggingStatusOf,
  isRunInProgress,
} from "./creative-tagging-status";

function latestRunStatus(
  data: LatestCreativeClassificationRunQuery | undefined,
): string | null | undefined {
  return data?.amp_creative_classification_runs[0]?.status;
}

/**
 * The workspace's latest tagging run for a namespace. Polls while a run is in
 * flight, and every `idlePollMs` otherwise so a run started elsewhere shows up.
 */
export function useCreativeTaggingStatus(
  namespace: CreativeTaggingNamespace,
  {
    enabled = true,
    idlePollMs,
  }: { enabled?: boolean; idlePollMs?: number } = {},
) {
  const { refetchInterval, resetBackoff } =
    useGovernedRefetchInterval<LatestCreativeClassificationRunQuery>({
      baseMs: 3000,
      compareKey: (polled) => latestRunStatus(polled),
    });
  const query = useLatestCreativeClassificationRunQuery(
    { namespace },
    {
      enabled,
      refetchInterval: (polled, pollQuery) =>
        isRunInProgress(latestRunStatus(polled))
          ? refetchInterval(polled, pollQuery)
          : (idlePollMs ?? false),
    },
  );
  return {
    status: creativeTaggingStatusOf(query.data),
    isLoading: query.isLoading,
    refetch: query.refetch,
    resetBackoff,
  };
}
